import type { WhiteLabelTab } from '@parkingbase/ecommerce-api-test';
import { MY_ACCOUNT_ROUTES, PUBLIC_ROUTES } from '@/routes/routes.constants';

/** Route of the page that renders a custom white-label tab's own `content`. */
export const WHITE_LABEL_CONTENT_ROUTE = PUBLIC_ROUTES.content;

/** Where a resolved white-label entry takes the parker when clicked. */
export type WhiteLabelNavTarget =
  | { kind: 'route'; href: string }
  | { kind: 'redirect'; url: string }
  | { kind: 'supportWidget' };

export type WhiteLabelBuiltInKey =
  | 'bookParking'
  | 'parkingValidation'
  | 'reservations'
  | 'myAccount'
  | 'contactUs'
  | 'aboutUs'
  | 'help';

export interface WhiteLabelNavEntry {
  /** Built-in the tab resolved to, or `custom` for an operator-created tab. */
  key: WhiteLabelBuiltInKey | 'custom';
  /** Raw tab title — the display label of a custom tab. */
  title: string;
  target: WhiteLabelNavTarget;
}

export interface WhiteLabelNav {
  header: WhiteLabelNavEntry[];
  footer: WhiteLabelNavEntry[];
}

export interface WhiteLabelNavContext {
  isLoggedIn: boolean;
  /** Discount-program role/membership or an event (legacy "Validate Parking" gate). */
  showParkingValidation: boolean;
  /** Company hourly toggle — gates the Reservations tab. */
  hourlyEnabled: boolean;
  /** Contact Us is hidden while a contact widget is configured (legacy AppHeader). */
  hasContactWidget: boolean;
}

interface BuiltIn {
  key: WhiteLabelBuiltInKey;
  placement: 'header' | 'footer';
  target: WhiteLabelNavTarget;
  isVisible: (ctx: WhiteLabelNavContext) => boolean;
}

const always = () => true;

/** Legacy AppHeader `menuList`, keyed by the tab title the back office stores. */
const BUILT_INS: Record<string, BuiltIn> = {
  'Book Parking': {
    key: 'bookParking',
    placement: 'header',
    target: { kind: 'route', href: '/' },
    isVisible: always,
  },
  'Validate Parking': {
    key: 'parkingValidation',
    placement: 'header',
    target: { kind: 'route', href: MY_ACCOUNT_ROUTES.parkingValidation },
    isVisible: (ctx) => ctx.showParkingValidation,
  },
  'My Account': {
    key: 'myAccount',
    placement: 'header',
    target: { kind: 'route', href: MY_ACCOUNT_ROUTES.overview },
    isVisible: (ctx) => ctx.isLoggedIn,
  },
  // Not in the legacy reservation-site menu: shown only when the operator adds a
  // "Reservations" tab, gated like My Account plus the company hourly toggle.
  Reservations: {
    key: 'reservations',
    placement: 'header',
    target: { kind: 'route', href: MY_ACCOUNT_ROUTES.reservations },
    isVisible: (ctx) => ctx.isLoggedIn && ctx.hourlyEnabled,
  },
  'About Us': {
    key: 'aboutUs',
    placement: 'footer',
    target: { kind: 'route', href: PUBLIC_ROUTES.about },
    isVisible: always,
  },
  'Contact Us': {
    key: 'contactUs',
    placement: 'footer',
    target: { kind: 'route', href: PUBLIC_ROUTES.contact },
    isVisible: (ctx) => !ctx.hasContactWidget,
  },
  Help: {
    key: 'help',
    placement: 'footer',
    target: { kind: 'supportWidget' },
    isVisible: always,
  },
};

/**
 * Legacy AppHeader shows its full built-in `menuList` when the company has no tabs.
 * Reservations was never in that list, so it stays opt-in.
 */
const LEGACY_DEFAULT_TABS: WhiteLabelTab[] = [
  'Book Parking',
  'Validate Parking',
  'My Account',
  'About Us',
  'Contact Us',
  'Help',
].map((title) => ({ title, standardTab: true }));

/**
 * Accepts an app-relative path (`/x`, never protocol-relative `//x`) or an absolute
 * http(s) URL. Anything else — `javascript:`, `data:` — is dropped, so a mistyped or
 * malicious redirection URL cannot execute script in the parker's session.
 */
export function isSafeRedirectionUrl(url: string): boolean {
  // Browsers read `/\host` and `/<tab>/host` as `//host` (same bypasses proxy.ts guards).
  if (url.startsWith('/')) return !/^\/[\\/\s]/.test(url);
  return /^https?:\/\//i.test(url);
}

/**
 * URL slug for a custom tab's page: `Rates & Fees` → `rates-fees`. Returns `''` for a
 * title with no letters or digits.
 */
export function toWhiteLabelSlug(title: string): string {
  return title
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Page route of a custom tab, or `null` when its title yields no slug. */
export function whiteLabelContentHref(title: string): string | null {
  const slug = toWhiteLabelSlug(title);
  return slug ? `${WHITE_LABEL_CONTENT_ROUTE}/${slug}` : null;
}

/**
 * Page a tab title opens: a built-in's dedicated route (`About Us` → `/about`), else the
 * custom tab's `/content/[slug]`; `null` when neither applies. Used to resolve legacy
 * `/content?title=` links, which the reservation site also generated for built-in titles.
 */
export function whiteLabelTabHref(title: string): string | null {
  const target = BUILT_INS[title]?.target;
  if (target) return target.kind === 'route' ? target.href : null;
  return whiteLabelContentHref(title);
}

const withLegacyDefault = (tabs: WhiteLabelTab[] | undefined | null): WhiteLabelTab[] =>
  tabs && tabs.length > 0 ? tabs : LEGACY_DEFAULT_TABS;

/**
 * The visible tab behind a built-in page (`About Us`, `Contact Us`), or `undefined` when
 * the back office hides it or has no such tab — the page must then not be entered.
 * Content emptiness is NOT a factor: legacy shows a visible tab's page even when empty.
 */
export function findVisibleBuiltInTab(
  tabs: WhiteLabelTab[] | undefined | null,
  title: 'About Us' | 'Contact Us'
): WhiteLabelTab | undefined {
  return withLegacyDefault(tabs).find((t) => t.title === title && !t.hidden);
}

/** The visible custom tab whose page slug is `slug`, or `undefined`. */
export function findVisibleCustomTab(
  tabs: WhiteLabelTab[] | undefined | null,
  slug: string
): WhiteLabelTab | undefined {
  if (!slug) return undefined;
  return (tabs ?? []).find(
    (t) => !!t.title && !t.hidden && !BUILT_INS[t.title] && toWhiteLabelSlug(t.title) === slug
  );
}

/**
 * Resolves the company's back-office white-label tabs into header and footer entries,
 * replicating the legacy reservation-site header (`AppHeader.getMenuList`). About Us,
 * Contact Us and Help go to the footer; every other tab stays in the header.
 *
 * ## Invariants:
 * - A tab whose own flags pass is still subject to its built-in's condition
 *   (Validate Parking needs a program role or event, My Account needs a signed-in user,
 *   Reservations needs a signed-in user and hourly on, Contact Us needs no contact
 *   widget). Back-office visibility never widens access.
 * - A tab titled like a built-in always opens that built-in's dedicated page (`/about`,
 *   `/contact`), even when the tab carries its own content — the reservation site on
 *   demo/master does the same, and the dedicated routes are the SEO-indexed ones.
 *   Only custom tabs use `/content/[slug]`.
 * - Reservations appears only when the back office lists a "Reservations" tab, at that
 *   tab's position. It is not in the legacy default list, so a company with no tabs (or
 *   the API default set) shows none.
 * - Order is the order the back office stores the tabs in; legacy never sorts.
 */
export function resolveWhiteLabelNav(
  tabs: WhiteLabelTab[] | undefined | null,
  ctx: WhiteLabelNavContext
): WhiteLabelNav {
  const nav: WhiteLabelNav = { header: [], footer: [] };

  for (const tab of withLegacyDefault(tabs)) {
    const title = tab.title ?? '';
    if (!title || tab.hidden) continue;
    if (tab.hiddenForSignedOutUser && !ctx.isLoggedIn) continue;

    const rawRedirect = tab.redirectionUrl?.trim() ?? '';
    const redirectionUrl = rawRedirect && isSafeRedirectionUrl(rawRedirect) ? rawRedirect : '';
    const builtIn = BUILT_INS[title];

    if (!builtIn) {
      const contentHref = whiteLabelContentHref(title);
      if (redirectionUrl) {
        nav.header.push({
          key: 'custom',
          title,
          target: { kind: 'redirect', url: redirectionUrl },
        });
      } else if (contentHref) {
        nav.header.push({ key: 'custom', title, target: { kind: 'route', href: contentHref } });
      }
      continue;
    }

    if (!builtIn.isVisible(ctx)) continue;

    const target: WhiteLabelNavTarget = redirectionUrl
      ? { kind: 'redirect', url: redirectionUrl }
      : builtIn.target;

    nav[builtIn.placement].push({ key: builtIn.key, title, target });
  }

  return nav;
}
